import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

interface HeaderProps {
  onMenuClick: () => void;
}

function LangToggle() {
  const { lang, toggle } = useLanguage();
  return (
    <button
      onClick={toggle}
      className="rounded border border-gold/30 px-2 py-0.5 text-[10px] font-bold text-gold/60 transition hover:border-gold hover:text-gold"
      title={lang === 'en' ? 'Switch to Tamil' : 'Switch to English'}
      aria-label={lang === 'en' ? 'Switch to Tamil' : 'Switch to English'}
    >
      {lang === 'en' ? 'தமிழ்' : 'EN'}
    </button>
  );
}

export default function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const initials = (user?.username || '?').slice(0, 2).toUpperCase();

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <header className="print:hidden h-14 bg-navy flex items-center justify-between px-4 md:px-6 flex-shrink-0 border-b border-navy-light">
      {/* Left — menu toggle (mobile) + church name */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          className="md:hidden text-gold text-lg leading-none focus:outline-none"
          onClick={onMenuClick}
          aria-label={t('header.openMenu')}
        >
          ☰
        </button>
        <span className="text-gold text-base sm:text-lg leading-none flex-shrink-0">✝</span>
        <span className="text-gold font-georgia text-xs sm:text-sm font-bold truncate">
          {t('header.churchName')}
        </span>
      </div>

      {/* Right — lang toggle + user avatar + dropdown */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <LangToggle />

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 focus:outline-none"
            aria-label={t('header.accountMenu')}
          >
            <div className="w-7 h-7 rounded-full bg-gold flex items-center justify-center text-navy font-georgia text-xs font-bold flex-shrink-0">
              {initials}
            </div>
            <span className="hidden sm:block text-blue-300 font-georgia text-xs truncate max-w-[100px]">
              {user?.username}
            </span>
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-10 w-44 bg-white shadow-card border border-grid-border z-50">
              <div className="px-4 py-3 border-b border-grid-border">
                <p className="text-navy text-xs font-georgia font-bold truncate">{user?.username}</p>
                <p className="text-teal text-xs font-georgia">{user?.role}</p>
              </div>
              <button
                onClick={async () => {
                  setMenuOpen(false);
                  await logout();
                  window.location.href = 'https://www.fatimashrinetambaram.in';
                }}
                className="w-full text-left px-4 py-2 text-xs font-georgia text-overdue-text hover:bg-offwhite"
              >
                {t('header.signOut')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
